/**
 * ReMixCharts.tsx - Optimized for Large Datasets
 *
 * Key optimizations:
 * 1. Data downsampling replaced with ECharts large/progressive rendering
 * 2. Eliminated JSON.stringify in useEffect deps (uses ref-based comparison)
 * 3. Cached maxValue computation, memoized event handlers
 * 4. Smart x-axis interval to prevent label overlap
 * 5. Second x-axis responds to dataZoom state for correct label density
 * 6. Progressive rendering & animation disabled for large data
 * 7. Proper zoom state preservation when toggling legends
 */

import React, {
  useState,
  useCallback,
  useMemo,
  useEffect,
  useRef,
} from "react";
import ReactECharts from "echarts-for-react";
import { SecondaryColor } from "../../common";
import {
  BAR_RADIUS,
  BrushStart,
  ChartArguements,
  ChartType,
  LegendKey,
} from "../../models/chart_model";
import { COST_UNIT } from "../../Units";
import { getTimeRange } from "../../pages/Dashboard/Exchange3/Chart";
import { useDispatch } from "react-redux";
import { setLoadingFilterName } from "../../store/state/FilterLoadingState";
import { getColor, getDistinctColorsForKeys } from "../../utility/Colors";
import { monthNames } from "../../pages/Dashboard/PriceForcating/IexMonthly";
import { normalizeDisplayLabel } from "../../utility/displayText";

const COLORS = [
  "#FF7F50",
  "#B8860B",
  "#7CB5EC",
  "#F1935C",
  "#34656D",
  "#333333",
];

/** Threshold above which large-data optimizations kick in */
const LARGE_DATA_THRESHOLD = 9000;

/** Shallow-compare two arrays of LegendKey by dataKey and name */
function legendsEqual(a: LegendKey[], b: LegendKey[]): boolean {
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) {
    if (a[i].dataKey !== b[i].dataKey || a[i].name !== b[i].name) return false;
  }
  return true;
}

export function lightenColor(color: string, percent: number): string {
  const num = parseInt(color.replace("#", ""), 16);
  const amt = Math.round(2.55 * percent);
  const R = (num >> 16) + amt;
  const G = ((num >> 8) & 0x00ff) + amt;
  const B = (num & 0x0000ff) + amt;
  return `#${(
    0x1000000 +
    (R < 255 ? (R < 1 ? 0 : R) : 255) * 0x10000 +
    (G < 255 ? (G < 1 ? 0 : G) : 255) * 0x100 +
    (B < 255 ? (B < 1 ? 0 : B) : 255)
  )
    .toString(16)
    .slice(1)}`;
}

const monthAbbrs = [
  "JAN",
  "FEB",
  "MAR",
  "APR",
  "MAY",
  "JUN",
  "JUL",
  "AUG",
  "SEP",
  "OCT",
  "NOV",
  "DEC",
];

const parseMonthKey = (name?: string): number | null => {
  if (!name) return null;
  const trimmed = name.trim();
  const head6 = trimmed.slice(0, 6);
  const match = /^([A-Za-z]{3})-(\d{2})$/.exec(head6);
  if (!match) return null;
  const monthIdx = monthAbbrs.indexOf(match[1].toUpperCase());
  if (monthIdx < 0) return null;
  const yy = parseInt(match[2], 10);
  return (2000 + yy) * 12 + monthIdx;
};

export const compareLegends = (
  aName: string,
  bName: string,
  customLegendOrder?: string[],
) => {
  const aKey = parseMonthKey(aName);
  const bKey = parseMonthKey(bName);
  if (aKey !== null && bKey !== null) return aKey - bKey;
  if (aKey !== null && bKey === null) return -1;
  if (aKey === null && bKey !== null) return 1;

  if (customLegendOrder) {
    const ai = customLegendOrder.indexOf(aName);
    const bi = customLegendOrder.indexOf(bName);
    if (ai >= 0 && bi >= 0) return ai - bi;
    if (ai >= 0 && bi < 0) return -1;
    if (ai < 0 && bi >= 0) return 1;
  }

  return aName.toLowerCase().localeCompare(bName.toLowerCase());
};

const getLegendColorKey = (legend: LegendKey, index = 0): string => {
  const dataKey = `${legend.dataKey ?? ""}`.trim();
  const name = `${legend.name ?? ""}`.trim();

  const key =
    dataKey.length > 0 ? dataKey : name.length > 0 ? name : `legend_${index}`;

  // Strip unit suffixes to maintain color consistency across filter changes (MW/MWh/LU)
  return key.replace(/_(mw|mwh|lu|rs_mwh)$/i, "");
};

const getLegendColor = (
  legend: LegendKey,
  index: number,
  selectedLegends: LegendKey[],
  isLegend?: boolean,
): string => {
  const isSelected =
    selectedLegends.some((item) => item.dataKey === legend.dataKey) ||
    selectedLegends.length === 0;
  const legendColorKey = getLegendColorKey(legend, index);
  return isSelected
    ? ((isLegend ? legend.legendColor : legend.stroke) ??
        legend.stroke ??
        getColor(index, legendColorKey))
    : "#E0E0E0";
};

export const LegendComponent = (
  legend: LegendKey,
  index: number,
  selectedLegends: LegendKey[],
  handleLegendClick: (legend: LegendKey) => void,
  circleOnly?: boolean,
  fontSize = 12,
  colorMap?: Record<string, string>,
  isSmallLegend = false,
) => {
  const isSelected =
    selectedLegends.some((item) => item.dataKey === legend.dataKey) ||
    selectedLegends.length === 0;
  const legendColorKey = getLegendColorKey(legend, index);
  const color = isSelected
    ? legend.legendColor ||
      legend.stroke ||
      colorMap?.[legendColorKey] ||
      getLegendColor(legend, index, selectedLegends, true)
    : "#E0E0E0";

  return (
    <div
      key={legend.dataKey}
      className={"line-legend"}
      style={{ fontSize: fontSize.toString() + `px` }}
      onClick={() => handleLegendClick(legend)}
    >
      <div style={{ color }}>
        <div
          className={
            legend.type === ChartType.Bar || circleOnly
              ? "dot"
              : legend.type === ChartType.Step
                ? "step"
                : legend.isDash
                  ? "dash"
                  : "line"
          }
          style={{
            marginRight: "6px",
            backgroundColor:
              legend.type === ChartType.Step || legend.isDash
                ? "transparent"
                : color,
            fontSize: isSmallLegend ? "10px!important" : undefined,
            width:
              isSmallLegend && legend.type !== ChartType.Bar
                ? "10px"
                : undefined,
            borderColor: color,
          }}
        ></div>
        {normalizeDisplayLabel(legend.name)}
      </div>
    </div>
  );
};

const formatAxisValue = (value: number): string => {
  const absValue = Math.abs(value);
  const sign = value < 0 ? "-" : "";
  if (absValue >= 1000000000) {
    const v = absValue / 1000000000;
    return (
      sign + (v >= 10 ? v.toFixed(0) : v.toFixed(1)).replace(/\.0$/, "") + "B"
    );
  } else if (absValue >= 1000000) {
    const v = absValue / 1000000;
    return (
      sign + (v >= 10 ? v.toFixed(0) : v.toFixed(1)).replace(/\.0$/, "") + "M"
    );
  } else if (absValue >= 1000) {
    const v = absValue / 1000;
    return (
      sign + (v >= 10 ? v.toFixed(0) : v.toFixed(1)).replace(/\.0$/, "") + "K"
    );
  } else if (absValue < 10 && absValue > 0) {
    if (absValue < 0.01) return value.toFixed(4);
    else if (absValue < 0.1) return value.toFixed(3);
    else if (absValue < 1) return value.toFixed(2);
    else return value.toFixed(1);
  } else if (value === 0) return "0";
  else if (value % 1 === 0) return sign + Math.round(absValue).toString();
  return value.toFixed(1);
};

export function ReMixChart({
  data,
  legends,
  onLegendSelect,
  syncid,
  unit = "",
  overflowLegend = false,
  xDataKey,
  xTick,
  xLabel,
  yAxisLabel,
  legendFontSize,
  secondYAxisLabel,
  yAxisWidth,
  legendBreakIndex,
  hidePrice,
  circleLegend = false,
  xAxisLabelPosition = "insideBottom",
  xAxisLabelOffset,
  onlyTitle = false,
  disableLegendClick = false,
  secondXDataKey,
  showBrush = true,
  brushIndex,
  labelFontSize,
  rendexSecondXTick,
  brushHeight = 17,
  brushStart = BrushStart.End,
  xaxisHeight = 35,
  secondYAxisWidth,
  secondXAxisHeight = 12,
  barCategoryGap = 2.5,
  xAxisTickMargin = 8,
  isTimeSlot,
  xAxisAngle,
  onBarClick,
  fontSize = 12,
  secondXAxisLabel,
  customTooltip,
  hiddenLegends = [],
  hiddenStats = [],
  enableMouseWheelZoom = true,
  chartContainerHeight = "",
  nameGap,
  maxYsize,
  showToolbox = true,
  yMaxValue,
  tooltipFilter,
  legendMaxHeight,
  isPriceForecast = false,
  defaultSelectedLegends = [],
  top = 30,
  bottom = 20,
  left,
  yAxisDomain,
  secondYAxisDomain,
  isSmallLegend = false,
  customLegendOrder,
  xAxisInterval,
  colorByPoint,
  barColors,
  highlightedXValues,
  enableClickSelection,
}: ChartArguements) {
  const [selectedLegends, setSelectedLegends] = useState<LegendKey[]>(
    defaultSelectedLegends.length > 0 ? defaultSelectedLegends : legends,
  );
  const lastZoom = useRef<{ start: number; end: number } | null>(null);
  /**
   * Reactive zoom range state — triggers re-render so 2nd x-axis labels
   * and smart x-axis interval recalculate when the brush is dragged.
   * `null` means "use the default brush position".
   */
  const [zoomRange, setZoomRange] = useState<{
    start: number;
    end: number;
  } | null>(null);
  /** Debounce timer for zoom state updates to avoid excessive re-renders */
  const zoomDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const dispatch = useDispatch();
  const chartRef = useRef<ReactECharts>(null);
  const prevDataRef = useRef<any[]>([]);
  const prevLegendsRef = useRef<LegendKey[]>([]);
  const prevDefaultLegendsRef = useRef<LegendKey[]>([]);

  // Replace JSON.stringify-based comparison with ref-based shallow compare
  useEffect(() => {
    if (data !== prevDataRef.current) {
      prevDataRef.current = data;
      // lastZoom.current = null;
      // setZoomRange(null); // reset zoom state when data changes
    }
  }, [data, brushIndex?.startIndex, brushIndex?.endIndex]);

  // Cleanup debounce timer on unmount
  useEffect(() => {
    const ref = zoomDebounceRef;
    return () => {
      if (ref.current) clearTimeout(ref.current);
    };
  }, []);

  useEffect(() => {
    const newLegends =
      defaultSelectedLegends.length > 0 ? defaultSelectedLegends : legends;
    const prevDefaults = prevDefaultLegendsRef.current;
    const prevLeg = prevLegendsRef.current;
    if (!legendsEqual(newLegends, selectedLegends)) {
      // Only update if legends actually changed
      if (
        !legendsEqual(defaultSelectedLegends, prevDefaults) ||
        !legendsEqual(legends, prevLeg)
      ) {
        setSelectedLegends(newLegends);
      }
    }
    prevDefaultLegendsRef.current = defaultSelectedLegends;
    prevLegendsRef.current = legends;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultSelectedLegends, legends]);

  const debouncedData = useMemo(() => {
    if (!data || data.length === 0) return [];
    return data;
  }, [data]);

  const buildSeries = useCallback(
    (
      chartData: any[],
      legendsToShow: LegendKey[],
      selected: LegendKey[],
      hidden: string[],
      colors: Record<string, string>,
      barGap: number,
      forecast: boolean,
      colorByPoint?: boolean,
      barColors?: string[],
    ) => {
      const isLarge = chartData.length > LARGE_DATA_THRESHOLD;
      return legendsToShow
        .filter((legend) => !hidden.includes(legend.name || ""))
        .map((legend, index) => {
          const legendColorKey = getLegendColorKey(legend, index);
          const isSelected =
            selected.some((item) => item.name === legend.name) ||
            selected.length === 0;
          const color = !isSelected
            ? getLegendColor(legend, index, selected)
            : (legend.legendColor ??
              legend.stroke ??
              (colors[legendColorKey] ||
                getLegendColor(legend, index, selected)));
          const seriesData = chartData.map(
            (item) => item[legend.dataKey] ?? null,
          );
          const commonOptions = {
            id: legend.dataKey,
            name: legend.name,
            data: seriesData,
            yAxisIndex: legend.yAxisId === "right" ? 1 : 0,
            itemStyle: { color },
            // Disable animation for large datasets to avoid UI freeze
            animation: !isLarge,
            ...(isLarge
              ? {
                  large: true,
                  largeThreshold: 2000,
                  progressive: 3000,
                  progressiveThreshold: 5000,
                }
              : {}),
          };

          if (legend.type === ChartType.Bar) {
            const customBarColor = "#f1935c";
            const barSeriesData = chartData.map((item, idx) => {
              const legendYear = (legend.dataKey || "").split("_")[0];
              if (forecast) {
                const now = new Date();
                const currentMonth = monthNames[now.getMonth()];
                const currentYear = now.getFullYear().toString();
                if (
                  item.monthGroup === currentMonth &&
                  legendYear === currentYear &&
                  item[legend.dataKey] != null
                ) {
                  return {
                    value: item[legend.dataKey],
                    itemStyle: { color: customBarColor },
                  };
                }
              }
              if (colorByPoint) {
                const palette =
                  barColors && barColors.length > 0 ? barColors : COLORS;
                return {
                  value: item[legend.dataKey],
                  itemStyle: { color: palette[idx % palette.length] },
                };
              }
              return { value: item[legend.dataKey] };
            });
            return {
              ...commonOptions,
              type: "bar",
              stack: legend.stackId || null,
              barGap: "0%",
              barCategoryGap: "20%",
              data: barSeriesData,
              // large mode skips individual bars on dense datasets — must be off
              // progressive is safe for bars and keeps rendering performant
              large: false,
              largeThreshold: undefined,
              itemStyle: {
                ...commonOptions.itemStyle,
                borderRadius: BAR_RADIUS,
              },
            };
          } else if (legend.type === ChartType.Step) {
            return {
              ...commonOptions,
              type: "line",
              step: legend.step || "start",
              stack: legend.stackId || null,
              symbol: "none",
              lineStyle: {
                width: 3,
                type: legend.isDash ? "dashed" : "solid",
              },
            };
          } else if (legend.type === ChartType.Area) {
            const areaGradient = {
              type: "linear" as const,
              x: 0,
              y: 0,
              x2: 0,
              y2: 1,
              colorStops: [
                { offset: 0, color: color + "CC" },
                { offset: 1, color: color + "10" },
              ],
            };
            return {
              ...commonOptions,
              type: "line",
              stack: legend.stackId || null,
              areaStyle: { color: areaGradient },
              symbol: "none",
              lineStyle: { width: 2, color },
            };
          } else {
            const isSinglePoint = seriesData.length === 1;
            return {
              ...commonOptions,
              // large + progressive only affect canvas rendering, NOT tooltip data
              // (sampling was the culprit for skipped tooltips — it's removed)
              ...(isLarge && !isSinglePoint
                ? {
                    large: true,
                    largeThreshold: 2000,
                    progressive: 3000,
                    progressiveThreshold: 5000,
                  }
                : {}),
              showSymbol: isSinglePoint,
              symbolSize: isSinglePoint ? 6 : undefined,
              hoverAnimation: false,
              animation: false,
              type: "line",
              smooth: !isSinglePoint,
              symbol: isSinglePoint ? "circle" : "none",
              lineStyle: { width: 2, type: legend.isDash ? "dashed" : "solid" },
            };
          }
        });
    },
    [],
  );

  const handleLegendClick = useCallback(
    (legend: LegendKey) => {
      if (disableLegendClick) return;
      setSelectedLegends((prevSelectedLegends) => {
        const allSelected =
          prevSelectedLegends.length === legends.length ||
          prevSelectedLegends.length === 0;
        let newSelectedLegends;
        if (allSelected) {
          newSelectedLegends = [legend];
        } else {
          const exists = prevSelectedLegends.some(
            (item) => item.dataKey === legend.dataKey,
          );
          newSelectedLegends = exists
            ? prevSelectedLegends.filter(
                (item) => item.dataKey !== legend.dataKey,
              )
            : [...prevSelectedLegends, legend];
        }
        if (onLegendSelect) onLegendSelect(newSelectedLegends);
        return newSelectedLegends;
      });
    },
    [disableLegendClick, onLegendSelect, legends],
  );

  const filteredLegends = useMemo(
    () => legends.filter((e) => e.dataKey !== xDataKey),
    [legends, xDataKey],
  );

  const colorMap = useMemo(() => {
    const legendColorKeys = filteredLegends.map((legend, index) =>
      getLegendColorKey(legend, index),
    );
    const distinctColors = getDistinctColorsForKeys(legendColorKeys) as Record<
      string,
      string
    >;
    const map: Record<string, string> = {};

    filteredLegends.forEach((legend, index) => {
      const legendColorKey = getLegendColorKey(legend, index);
      map[legendColorKey] =
        distinctColors[legendColorKey] ?? getColor(index, legendColorKey);
    });

    return map;
  }, [filteredLegends]);

  const orderedSelectedLegends = useMemo(() => {
    const base =
      selectedLegends.length === 0
        ? filteredLegends
        : legends.filter((legend) =>
            selectedLegends.some(
              (selected) => selected.dataKey === legend.dataKey,
            ),
          );
    return [...base].sort((a, b) =>
      compareLegends(a.name || "", b.name || "", customLegendOrder),
    );
  }, [selectedLegends, filteredLegends, legends, customLegendOrder]);

  const xAxisData = useMemo(
    () => debouncedData?.map((item) => item[xDataKey]) || [],
    [debouncedData, xDataKey],
  );

  const tooltipFormatter = useCallback(
    (params: any) => {
      const param = Array.isArray(params) ? params[0] : params;
      let items = Array.isArray(params) ? params : [params];
      if (tooltipFilter)
        items = items.filter((item) => tooltipFilter(item, param));

      const cleanName =
        typeof param.name === "string"
          ? normalizeDisplayLabel(param.name)
          : param.name;
      let title = cleanName;
      if (secondXDataKey && debouncedData[param.dataIndex]?.[secondXDataKey]) {
        title = isTimeSlot
          ? `${debouncedData[param.dataIndex][secondXDataKey]} - ${cleanName} (${getTimeRange(parseInt(param.name))})`
          : `${debouncedData[param.dataIndex][secondXDataKey]} - ${cleanName}`;
      } else if (isTimeSlot) {
        title = getTimeRange(parseInt(param.name)) + ` (${param.name})`;
      }

      const hasMonthKeys = items.some(
        (item) => parseMonthKey(item.seriesName || "") !== null,
      );
      const sortedItems = [...items].sort((a, b) =>
        compareLegends(
          a.seriesName || "",
          b.seriesName || "",
          customLegendOrder,
        ),
      );
      const validItems = sortedItems.filter((item) => {
        if (item.seriesName?.startsWith("__")) return false;
        if (item.value === null || item.value === undefined) return false;
        const value = parseFloat(item.value);
        return !isNaN(value) && isFinite(value);
      });

      const now = new Date();
      const currentMonth = monthNames[now.getMonth()];
      const currentYear = now.getFullYear().toString();

      const tooltipItems = validItems.map((item) => {
        const year = item.seriesName?.trim();
        let displaySeriesName = item.seriesName;
        if (
          isPriceForecast &&
          item.name === currentMonth &&
          year === currentYear
        ) {
          displaySeriesName = `${currentYear} Forecast`;
        }

        const value = parseFloat(item.value)
          .toFixed(2)
          .replace(/\B(?=(\d{3})+(?!\d))/g, ",");
        const bgColor = item.color;
        const legend = legends.find((l) => l.name === item.seriesName);

        let suffix = "";
        if (legend?.unit !== undefined) {
          suffix = legend.unit;
        } else {
          const suffixDict = {
            Price: COST_UNIT,
            Percent: "%",
            "%": "%",
            Cost: "Rs",
            Revenue: "Rs",
          };
          const foundKey = Object.keys(suffixDict).find((key) =>
            item.seriesName?.toString().includes(key),
          );
          suffix = foundKey
            ? suffixDict[foundKey as keyof typeof suffixDict]
            : legend?.unit || unit || "";
        }
        return `
    <div class="tooltip-item" data-name="${displaySeriesName?.toLowerCase()}">
      <div class="color-indicator" style="background-color: ${bgColor}"></div>
      <div class="series-name">${normalizeDisplayLabel(
        hidePrice ? displaySeriesName.replace("Price", "") : displaySeriesName,
      )}</div>
      <div class="value">${value} ${suffix}</div>
    </div>
  `;
      });

      return `<div class="enhanced-tooltip"><div class="tooltip-header"><div class="tooltip-title">${title}</div></div><div class="tooltip-content">${tooltipItems.join("")}</div></div><style>.enhanced-tooltip{font-family:'Inter',system-ui,sans-serif;width:100%;min-width:200px}.tooltip-header{display:flex;flex-direction:column;gap:8px;border-bottom:1px solid #eaeaea;padding-bottom:8px;margin-bottom:8px}.tooltip-title{font-weight:600;font-size:${fontSize + 1}px;color:#333}.tooltip-content{max-height:300px;overflow-y:auto;padding-right:10px}.tooltip-item{display:flex;align-items:center;padding:4px 0;border-bottom:1px solid #f0f0f0}.tooltip-item:last-child{border-bottom:none}.color-indicator{width:10px;height:10px;border-radius:50%;margin-right:8px}.series-name{flex:1;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;font-size:${fontSize}px}.value{font-weight:500;margin-left:10px;font-size:${fontSize}px}</style>`;
    },
    [
      tooltipFilter,
      secondXDataKey,
      debouncedData,
      isTimeSlot,
      fontSize,
      isPriceForecast,
      legends,
      hidePrice,
      unit,
      customLegendOrder,
    ],
  );

  const chartOptions = useMemo(() => {
    if (!debouncedData || debouncedData.length === 0) return {};

    const isLarge = debouncedData.length > LARGE_DATA_THRESHOLD;

    const series = buildSeries(
      debouncedData,
      orderedSelectedLegends,
      selectedLegends,
      hiddenStats,
      colorMap,
      barCategoryGap,
      isPriceForecast,
      colorByPoint,
      barColors,
    );

    // Compute maxValue once for both click & highlight layers
    let cachedMaxValue: number | null = null;
    const getMaxValue = () => {
      if (cachedMaxValue !== null) return cachedMaxValue;
      let max = 1000;
      const activeLegends = orderedSelectedLegends.filter(
        (leg) => !hiddenStats.includes(leg.name || ""),
      );
      // Sample at most 500 points for max calculation on large datasets
      const step = isLarge
        ? Math.max(1, Math.floor(debouncedData.length / 500))
        : 1;
      for (let i = 0; i < debouncedData.length; i += step) {
        for (const leg of activeLegends) {
          const v = Number(debouncedData[i][leg.dataKey]);
          if (!isNaN(v) && v > max) max = v;
        }
      }
      cachedMaxValue = max;
      return max;
    };

    // Add invisible click-capture layer if enabled
    if (enableClickSelection && debouncedData.length > 0) {
      const maxValue = getMaxValue();
      series.push({
        name: "__click_layer__",
        type: "bar",
        barGap: "-100%",
        data: debouncedData.map(() => maxValue * 1.2),
        itemStyle: {
          color: "transparent",
        },
        z: 100,
        cursor: "pointer",
        yAxisIndex: 0,
        legendHoverLink: false,
        animation: false,
      } as any);
    }

    // Add highlight layer for selected blocks
    if (
      highlightedXValues &&
      highlightedXValues.length > 0 &&
      debouncedData.length > 0
    ) {
      const maxValue = getMaxValue();
      // Pre-build a Set for O(1) lookup instead of .some() per item
      const highlightSet = new Set(highlightedXValues.map(String));
      series.push({
        name: "__highlight_layer__",
        type: "bar",
        barGap: "-100%",
        data: debouncedData.map((item) => {
          return highlightSet.has(String(item[xDataKey])) ? maxValue * 1.2 : 0;
        }),
        itemStyle: {
          color: "rgba(238, 244, 254, 0.75)",
        },
        z: 10,
        silent: true,
        yAxisIndex: 0,
        legendHoverLink: false,
        animation: false,
      } as any);
    }

    const brushStartIndex =
      brushStart === BrushStart.Start
        ? 0
        : Math.max(0, debouncedData.length - 96);
    const brushEndIndex =
      brushStart === BrushStart.Start
        ? Math.min(95, debouncedData.length - 1)
        : Math.max(0, debouncedData.length - 1);

    const defaultStartPercent =
      brushIndex?.startIndex !== undefined
        ? (brushIndex.startIndex / debouncedData.length) * 100
        : (brushStartIndex / debouncedData.length) * 100;
    const defaultEndPercent =
      brushIndex?.endIndex !== undefined
        ? ((brushIndex.endIndex + 1) / debouncedData.length) * 100
        : ((brushEndIndex + 1) / debouncedData.length) * 100;

    // Use reactive zoomRange state (drives re-render on brush drag)
    // Fall back to lastZoom ref (preserves zoom across legend toggles)
    // Fall back to default brush position
    const zoomStart =
      zoomRange?.start ?? lastZoom.current?.start ?? defaultStartPercent;
    const zoomEnd =
      zoomRange?.end ?? lastZoom.current?.end ?? defaultEndPercent;

    const dataZoomXAxisIndex = secondXDataKey ? [0, 1] : [0];

    // Compute visible data count based on zoom window
    const visibleFraction = Math.max(0.01, (zoomEnd - zoomStart) / 100);
    const visibleDataCount = Math.ceil(debouncedData.length * visibleFraction);

    /**
     * Smart x-axis interval: prevents label overlap by calculating
     * how many labels fit given the visible data window.
     */
    const computeSmartXInterval = (): number | "auto" => {
      if (xAxisInterval !== undefined) return xAxisInterval;
      if (isTimeSlot) return "auto";
      // For small visible windows, show all labels
      if (visibleDataCount <= 30) return "auto";
      // For medium data, space labels so ~25-30 are visible
      if (visibleDataCount <= 60) return Math.floor(visibleDataCount / 5) - 1;
      if (visibleDataCount <= 90) return Math.floor(visibleDataCount / 8) - 1;
      if (visibleDataCount <= 180) return Math.floor(visibleDataCount / 15) - 1;
      if (visibleDataCount <= 270) return Math.floor(visibleDataCount / 22) - 1;
      if (visibleDataCount <= 365) return Math.floor(visibleDataCount / 30) - 1;
      if (visibleDataCount <= 450) return Math.floor(visibleDataCount / 45) - 1;
      return Math.floor(visibleDataCount / 60) - 1;
    };

    // Second X axis configuration — zoom-aware label density
    const secondXAxisConfig = secondXDataKey
      ? (() => {
          const secondXAxisData = debouncedData.map(
            (item) => item[secondXDataKey],
          );
          const uniqueDates = [...new Set(secondXAxisData)];
          const totalDates = uniqueDates.length;
          const blocksPerDate = Math.max(
            1,
            Math.floor(debouncedData.length / totalDates),
          );
          const centerPosition = Math.floor(blocksPerDate / 2);

          // Use zoom-aware visible range
          const visibleStart = Math.floor(
            (zoomStart / 100) * debouncedData.length,
          );
          const visibleEnd = Math.min(
            Math.ceil((zoomEnd / 100) * debouncedData.length),
            debouncedData.length - 1,
          );
          const zoomVisibleLength = visibleEnd - visibleStart + 1;
          const visibleDateCount = Math.max(
            1,
            Math.ceil(zoomVisibleLength / blocksPerDate),
          );

          const getSkipFactor = (count: number): number => {
            if (count <= 7) return 1;
            if (count <= 14) return 2;
            if (count <= 30) return 3;
            if (count <= 60) return 5;
            if (count <= 90) return 8;
            if (count <= 180) return 10;
            if (count <= 270) return 14;
            if (count <= 365) return 18;
            if (count <= 450) return 22;
            return 60;
          };

          const getDateInterval = (index: number): boolean => {
            const dateGroupIndex = Math.floor(index / blocksPerDate);
            const isAtCenterOfDateGroup =
              index % blocksPerDate === centerPosition;
            if (!isAtCenterOfDateGroup) return false;
            const skipFactor = getSkipFactor(visibleDateCount);
            return dateGroupIndex % skipFactor === 0;
          };

          // Adapt font size to how many dates are visible
          const dateLabelFontSize =
            visibleDateCount > 100
              ? Math.max(8, fontSize - 2.5)
              : visibleDateCount > 20
                ? fontSize - 1
                : visibleDateCount > 15
                  ? fontSize - 0.5
                  : fontSize;

          return [
            {
              axisLine: { lineStyle: { color: "#E0E0E0" } },
              type: "category",
              axisPointer: { show: false },
              position: "bottom",
              nameLocation: "end",
              nameTextStyle: {
                fontSize: labelFontSize ?? fontSize + 3,
                color: SecondaryColor,
              },
              axisLabel: {
                color: "#8C8A96",
                interval: getDateInterval,
                fontSize: dateLabelFontSize,
                // hideOverlap prevents any remaining label collisions
                hideOverlap: true,
              },
              offset: 25,
              data: secondXAxisData,
            },
          ];
        })()
      : [];

    return {
      // Disable animation globally for large datasets
      animation: !isLarge,
      ...(isLarge
        ? {
            progressive: 3000,
            progressiveThreshold: 5000,
          }
        : {}),
      grid: {
        left: left ?? 40,
        right: 50,
        bottom: showBrush
          ? brushHeight + xaxisHeight - (isSmallLegend ? 30 : 10)
          : xaxisHeight - bottom,
        top: top || 20,
        containLabel: true,
      },
      tooltip: {
        trigger: "axis",
        axisPointer: {
          type: "line",
          snap: true,
          label: { show: false },
          xAxisIndex: 0,
          // Disable axisPointer animation to reduce rendering overhead
          animation: false,
        },
        formatter: customTooltip || tooltipFormatter,
        enterable: true,
        confine: true,
        appendToBody: true,
        backgroundColor: "#fff",
        borderRadius: 10,
        textStyle: { fontSize, fontWeight: 500, color: "#333" },
        extraCssText:
          "box-shadow: 0px 0px 10px 0px rgba(0,0,0,0.1);border:none;max-height:calc(100vh - 350px);overflow-y:auto;padding-right:10px",
        showDelay:
          visibleDataCount > 2000
            ? 80
            : visibleDataCount > 500
              ? 40
              : visibleDataCount > 200
                ? 15
                : 0,
        // Eliminate tooltip move animation — makes snapping feel instant
        transitionDuration: 0.4,
      },

      legend: {
        show: false,
        data: series
          .filter((s: any) => !String(s.name).startsWith("__"))
          .map((s: any) => s.name),
      },

      toolbox: {
        show: showToolbox ?? true,
        top: -5,
        feature: {
          dataZoom: { yAxisIndex: "none" },
          restore: {},
          saveAsImage: {},
        },
      },
      xAxis: [
        {
          type: "category",
          data: xAxisData,
          axisLabel: {
            fontSize,
            rotate: xAxisAngle || 0,
            margin: xAxisTickMargin + 4,
            color: "#8C8A96",
            interval: computeSmartXInterval(),
            formatter: (value: string) =>
              typeof value === "string" ? normalizeDisplayLabel(value) : value,
            // hideOverlap prevents labels from overlapping when the smart
            // interval still results in collisions (e.g. long labels)
            hideOverlap: true,
            showMinLabel: true,
            showMaxLabel: true,
          },
          axisLine: { lineStyle: { color: "#E0E0E0" } },
          axisTick: {
            show: true,
            alignWithLabel: true,
            length: 4,
            lineStyle: { color: "#E0E0E0" },
          },
          name: xLabel,
          nameLocation:
            xAxisLabelPosition === "insideBottom"
              ? "middle"
              : xAxisLabelPosition,
          nameGap: xAxisLabelOffset || 15,
          nameTextStyle: {
            fontSize: labelFontSize ?? fontSize + 3,
            color: SecondaryColor,
          },
        },
        ...secondXAxisConfig,
      ],
      yAxis: [
        {
          type: "value",
          ...(yMaxValue ? { max: yMaxValue } : {}),
          splitNumber: 5,
          axisLabel: {
            fontSize,
            margin: 8,
            formatter: formatAxisValue,
            hideOverlap: true,
          },
          axisTick: { show: true, length: 4, lineStyle: { color: "#E0E0E0" } },
          scale: false,
          boundaryGap: [0, 0.1],
          name: yAxisLabel,
          nameLocation: "middle",
          nameGap: nameGap ?? 50,
          nameTextStyle: {
            fontSize: labelFontSize ?? fontSize,
            color: SecondaryColor,
            fontWeight: 600,
            fontFamily: "Inter, system-ui, sans-serif",
          },
        },
        {
          type: "value",
          position: "right",
          ...(secondYAxisDomain
            ? { min: secondYAxisDomain[0], max: secondYAxisDomain[1] }
            : {}),
          alignTicks: true,
          splitNumber: 5,
          axisLabel: {
            fontSize,
            margin: 8,
            formatter: formatAxisValue,
            hideOverlap: true,
          },
          axisTick: { show: true, length: 4, lineStyle: { color: "#E0E0E0" } },
          scale: false,
          boundaryGap: [0, 0.1],
          name: secondYAxisLabel,
          nameLocation: "middle",
          nameGap: nameGap ?? 50,
          nameTextStyle: {
            fontSize: labelFontSize ?? fontSize,
            color: SecondaryColor,
            fontWeight: 600,
            fontFamily: "Inter, system-ui, sans-serif",
          },
        },
      ],
      series,
      // KEY FIX: Proper xAxisIndex configuration with preserved zoom state
      dataZoom: [
        ...(showBrush
          ? [
              {
                type: "slider",
                start: zoomStart,
                end: zoomEnd,
                height: brushHeight,
                width: "98%",
                bottom: 10,
                left: 10,
                brushSelect: false,
                xAxisIndex: dataZoomXAxisIndex,
                textStyle: { color: SecondaryColor },
                handleIcon:
                  "path://M10.7,11.9v-1.3H9.3v1.3c-4.9,0.3-8.8,4.4-8.8,9.4c0,5,3.9,9.1,8.8,9.4v1.3h1.3v-1.3c4.9-0.3,8.8-4.4,8.8-9.4C19.5,16.3,15.6,12.2,10.7,11.9z M13.3,24.4H6.7V23h6.6V24.4z M13.3,19.6H6.7v-1.4h6.6V19.6z",
                handleSize: "80%",
                // filterMode "none" prevents re-filtering series data on zoom
                // — critical for large datasets to avoid UI freeze
                filterMode: "none" as const,
                // throttle slider events on large data to avoid frame drops
                ...(isLarge ? { throttle: 100 } : {}),
              },
            ]
          : []),
        ...(enableMouseWheelZoom
          ? [
              {
                type: "inside",
                start: zoomStart,
                end: zoomEnd,
                xAxisIndex: dataZoomXAxisIndex,
                zoomLock: false,
                zoomOnMouseWheel: true,
                moveOnMouseMove: true,
                preventDefaultMouseMove: false,
                filterMode: "none" as const,
                // throttle wheel zoom on large data
                ...(isLarge ? { throttle: 80 } : {}),
              },
            ]
          : []),
      ],
    };
  }, [
    debouncedData,
    orderedSelectedLegends,
    selectedLegends,
    hiddenStats,
    colorMap,
    barCategoryGap,
    isPriceForecast,
    brushStart,
    left,
    showBrush,
    brushHeight,
    xaxisHeight,
    isSmallLegend,
    bottom,
    top,
    customTooltip,
    tooltipFormatter,
    fontSize,
    showToolbox,
    xAxisData,
    xAxisAngle,
    xAxisTickMargin,
    xLabel,
    xAxisLabelPosition,
    xAxisLabelOffset,
    labelFontSize,
    secondXDataKey,
    yMaxValue,
    yAxisLabel,
    nameGap,
    secondYAxisLabel,
    brushIndex?.startIndex,
    brushIndex?.endIndex,
    enableMouseWheelZoom,
    secondYAxisDomain,
    buildSeries,
    colorByPoint,
    barColors,
    enableClickSelection,
    highlightedXValues,
    xDataKey,
    xAxisInterval,
    isTimeSlot,
    zoomRange,
  ]);

  const onChartEvents = useMemo(
    () => ({
      click: (params: any) => {
        // Ignore clicks on internal layers
        if (params.seriesName?.startsWith("__")) {
          if (onBarClick) {
            const clickedData = debouncedData[params.dataIndex];
            if (clickedData) {
              onBarClick({
                ...params,
                name: clickedData[xDataKey],
                seriesName: "user_click",
                data: clickedData,
              });
            }
          }
        } else if (onBarClick) {
          onBarClick(params);
        }
      },
      datazoom: (params: any) => {
        // Capture zoom state into ref immediately (no re-render)
        let start: number | undefined;
        let end: number | undefined;
        const zoom = params.batch ? params.batch[0] : params;
        if (zoom.start !== undefined && zoom.end !== undefined) {
          start = zoom.start;
          end = zoom.end;
        } else if (chartRef.current) {
          const chartInstance = chartRef.current.getEchartsInstance();
          const option = chartInstance.getOption();
          const dz = option.dataZoom as any[];
          if (dz && dz.length > 0) {
            start = dz[0].start;
            end = dz[0].end;
          }
        }
        if (start !== undefined && end !== undefined) {
          lastZoom.current = { start, end };
          // Debounced state update — triggers re-render so 2nd x-axis
          // labels recalculate based on new brush position/size.
          // 150ms debounce avoids flooding React with renders while dragging.
          if (zoomDebounceRef.current) clearTimeout(zoomDebounceRef.current);
          zoomDebounceRef.current = setTimeout(() => {
            setZoomRange({ start: start!, end: end! });
          }, 150);
        }
      },
      finished: () => {
        requestAnimationFrame(() => dispatch(setLoadingFilterName("")));
      },
    }),
    [onBarClick, debouncedData, xDataKey, dispatch],
  );

  return (
    <>
      <div
        style={{ height: chartContainerHeight }}
        className="chart-container tab-animatable-content"
      >
        <style
          dangerouslySetInnerHTML={{
            __html: `@media (max-width: 768px){.chart-container{height:350px !important}.enhanced-tooltip{min-width:180px !important}.tooltip-title{font-size:${fontSize - 2}px !important}.series-name,.value{font-size:${fontSize - 3}px !important}.tooltip-content{max-height:200px !important}}`,
          }}
        />
        <ReactECharts
          ref={chartRef}
          option={chartOptions}
          style={{ height: "100%", width: "100%" }}
          opts={{ renderer: "canvas" }}
          lazyUpdate={true}
          onEvents={onChartEvents}
          notMerge={true}
        />
      </div>

      <div
        style={{ maxHeight: legendMaxHeight || "17%" }}
        className={`flex flex-wrap justify-center gap-x-2 overflow-legend tab-animatable-content ${
          showBrush && !isSmallLegend && "pt-1"
        }`}
      >
        <style
          dangerouslySetInnerHTML={{
            __html: `
          @media (max-width: 768px) {
            .overflow-legend {
              gap: 0.5rem !important;
              padding: 0.5rem !important;
            }
            .line-legend {
              font-size: ${legendFontSize ? legendFontSize - 2 : fontSize - 2}px !important;
            }
          }
          @media (max-width: 480px) {
            .overflow-legend {
              gap: 0.25rem !important;
              padding: 0.25rem !important;
            }
            .line-legend {
              font-size: ${legendFontSize ? legendFontSize - 3 : fontSize - 3}px !important;
            }
          }
        `,
          }}
        />
        {filteredLegends
          .slice()
          .sort((a, b) =>
            compareLegends(a.name || "", b.name || "", customLegendOrder),
          )
          .map((legend, index) => {
            const isHidden = hiddenLegends.includes(legend.name ?? "");
            return (
              <React.Fragment key={index}>
                <div className={`mb-3 ${isHidden ? "hidden" : ""}`}>
                  {LegendComponent(
                    legend,
                    index,
                    selectedLegends,
                    handleLegendClick,
                    circleLegend,
                    legendFontSize,
                    colorMap,
                    isSmallLegend,
                  )}
                </div>
                {legendBreakIndex != 0 && legendBreakIndex === index && (
                  <div style={{ width: "100%", height: "2px" }}></div>
                )}
              </React.Fragment>
            );
          })}
      </div>
    </>
  );
}
